import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import http from '@/libs/http'
import dayjs from 'dayjs'
import { IModule } from './modules.store'
import { errorToaster } from '@/libs/errorToaster'

export interface IHub {
	_id: string
	serial: string
	locationId: string
	name: string
	state: string
	alertInterval: number
	lastLog: string
	logOffsetHours: number
	modified: number
	created: string
	currentSet?: any
	// other hub-specific fields
	[key: string]: any
}

export const useHubStore = defineStore('hub', () => {
	// --- STATE ---
	const hubs = ref<IHub[]>([])
	const modules = ref<IModule[]>([])
	const loading = ref<Record<string, boolean>>({})
	const loadingMap = ref<Record<string, boolean>>({})
	const errorMap = ref<Record<string, string>>({})
	// --- HELPERS ---

	const formatError = (error: any): string =>
		error?.response?.data?.msg || error?.response?.data || error.message || 'Unknown error'

	async function withLoading<T>(key: string, fn: () => Promise<T>, shouldToast: boolean = false): Promise<T> {
		if (loadingMap.value[key]) {
			throw new Error(`🟧 ${key} is already in progress (hubStore)`)
		}
		loadingMap.value[key] = true
		delete errorMap.value[key]
		try {
			return await fn()
		} catch (e: any) {
			if (e?.name === 'AbortError') {
				console.warn(`🟨 ${key} was aborted (hubStore)`)
				throw e
			}
			const message = formatError(e)
			console.error(`🟥 Error in ${key} (hubStore):`, e)
			errorMap.value[key] = message
			if (shouldToast) errorToaster(key, message)
			throw message
		} finally {
			delete loadingMap.value[key]
		}
	}

	// --- GETTERS ---

	// const hubsByLocLastOnline = computed(() => {
	// 	const result: Record<string, { modified: number; diff: string }> = {}
	// 	for (const hub of hubs.value) {
	// 		const locId = hub.locationId || 'NO_LOCATION'
	// 		const hubTime = dayjs(hub.lastLog).unix() * 1000
	// 		const diff = dayjs(hubTime).fromNow()
	// 		if (!result[locId] || result[locId].modified < hubTime) {
	// 			result[locId] = { modified: hubTime, diff }
	// 		}
	// 	}
	// 	return result
	// })

	const hubsByLoc = computed(() => {
		const result: Record<string, IHub[]> = {}
		for (const hub of hubs.value) {
			const locId = hub.locationId || 'NO_LOCATION'
			if (!result[locId]) result[locId] = []
			result[locId].push({ ...hub })
		}
		return result
	})

	// --- ACTIONS ---

	let _listAllHubsPromise: Promise<IHub[]> | null = null
	const listAllHubs = async () => {
		if (_listAllHubsPromise) return _listAllHubsPromise
		_listAllHubsPromise = withLoading('listAllHubs', async () => {
			console.log('🍍 listAllHubs')
			const resp = await http.getAuth<IHub[]>('/api/v2/hubs')
			hubs.value = resp || []
			return hubs.value
		}).finally(() => { _listAllHubsPromise = null })
		return _listAllHubsPromise
	}

	// async function getHub(hubId: string) {
	const getHub = async (hubId: string) =>
		withLoading('getHub', async () => {
			console.log('🍍 getHub')
			const resp = await http.getAuth<IHub[]>(`/api/v2/hub:${hubId}`)
			return resp || null
		})

	// async function listHubsByProject(prolocId: string) {
	// 	return withLoading('listHubsByProject', async () => {
	// 		try {
	// 			const resp = await http.getAuth<IHub[]>(`/api/v2/hubs/by/${prolocId}`)
	// 			hubs.value = resp || []
	// 			return hubs.value
	// 		} catch (error: any) {
	// 			console.error('listHubsByProject', error)
	// 			throw new Error(formatError(error))
	// 		}
	// 	})
	// }

	const updateHub = async (data: IHub) =>
		withLoading(
			`updateHub-${data._id}`,
			async () => {
				console.log('🍍 updateHub', data)
				const resp = await http.postAuth<{ msg: string }, IHub>('/api/v2/hubs', data)
				// await listAllHubs()
				return resp.msg
			},
			true,
		)

	const downloadHubCsvData = async (params: {
		hubSerial?: string
		graphId?: string
		from: string | number
		to: string | number
		format: 'csv' | 'json' | 'tsv'
		original?: boolean
	}) =>
		withLoading('downloadHubCsvData', async () => {
			const { hubSerial, graphId, from, to, format, original } = params
			const id = hubSerial || graphId
			const resp = await http.getAuth<string>(`/api/v1/export/${format}/hubs/${id}/${from}/${to}/${original}`)
			// Build download link
			let content = ''
			if (format === 'csv') content = 'data:text/csv;charset=utf-8,' + resp
			if (format === 'json') content = 'data:application/json;charset=utf-8,' + resp
			if (format === 'tsv') content = 'data:text/tsv;charset=utf-8,' + resp

			const encoded = encodeURI(content)
			const a = document.createElement('a') as HTMLAnchorElement
			a.href = encoded
			a.download = `data-${id}-${from}-${to}.${format}`
			document.body.appendChild(a)
			a.click()
			a.remove()

			return true
		})

	// --- INITIAL LOAD CAN BE TRIGGERED IN COMPONENTS ---
	// listAllHubs()
	const _init = async () => {
		await listAllHubs()
	}
	_init()

	return {
		hubs,
		modules,
		loading,
		loadingMap,
		errorMap,

		// hubsByLocLastOnline,
		hubsByLoc,

		listAllHubs,
		getHub,
		// listHubsByProject,

		updateHub,

		downloadHubCsvData,
	}
})
